import { Station } from '../stations'
import { SupportedLanguageCode, LANGUAGE_CODES, getTranslation, updateTranslation } from './i18n'

// 未設定表示用の定数
export const NOT_SET_TEXT = '未設定'

// ステーション翻訳データ型（API仕様に基づく）
export interface StationTranslation {
  langCode: string
  name?: string
  extra?: string
  fullAddress?: string
}

// ステーション用翻訳ヘルパー関数
export const getStationTranslation = (
  translations: StationTranslation[] | undefined,
  selectedLanguageCode: SupportedLanguageCode
): StationTranslation | undefined => {
  return getTranslation(translations, selectedLanguageCode)
}

// ステーション名の表示用ヘルパー関数
export const getStationDisplayName = (
  station: Station,
  langCode: SupportedLanguageCode
): string => {
  // 日本語の場合は元のフィールドを使用
  if (langCode === LANGUAGE_CODES.JAPANESE) {
    return station.name || ''
  }

  // 翻訳配列から取得を試す
  const translation = getStationTranslation(station.stationTranslations, langCode)
  if (translation?.name) {
    return translation.name
  }

  // 翻訳がない場合は未設定を返す（フォールバックしない）
  return NOT_SET_TEXT
}

// ステーション住所の表示用ヘルパー関数
export const getStationDisplayAddress = (
  station: Station,
  langCode: SupportedLanguageCode
): string => {
  // 日本語の場合は元のフィールドを使用
  if (langCode === LANGUAGE_CODES.JAPANESE) {
    return station.fullAddress || ''
  }

  // 翻訳配列から取得を試す
  const translation = getStationTranslation(station.stationTranslations, langCode)
  if (translation?.fullAddress) {
    return translation.fullAddress
  }

  // 翻訳がない場合は未設定を返す（フォールバックしない）
  return NOT_SET_TEXT
}

// ステーション備考の表示用ヘルパー関数
export const getStationDisplayExtra = (
  station: Station,
  langCode: SupportedLanguageCode
): string => {
  // 日本語の場合は元のフィールドを使用
  if (langCode === LANGUAGE_CODES.JAPANESE) {
    return station.extra || ''
  }

  // 翻訳配列から取得を試す
  const translation = getStationTranslation(station.stationTranslations, langCode)
  if (translation?.extra) {
    return translation.extra
  }

  // 翻訳がない場合は未設定を返す（フォールバックしない）
  return NOT_SET_TEXT
}

// 空の翻訳データを作成するヘルパー関数
export const createEmptyStationTranslations = (): StationTranslation[] => [
  { langCode: LANGUAGE_CODES.JAPANESE, name: '', extra: '', fullAddress: '' },
  { langCode: LANGUAGE_CODES.ENGLISH, name: '', extra: '', fullAddress: '' },
]

// 新しい翻訳エントリを作成するヘルパー関数
export const createStationTranslation = (
  langCode: SupportedLanguageCode,
  field: 'name' | 'extra' | 'fullAddress',
  value: string
): StationTranslation => {
  return {
    langCode,
    [field]: value,
  }
}

// 翻訳データを更新するヘルパー関数
export const updateStationTranslation = (
  translations: StationTranslation[] | undefined,
  langCode: string,
  field: 'name' | 'extra' | 'fullAddress',
  value: string
): StationTranslation[] => {
  const currentTranslations = translations || []
  return updateTranslation(currentTranslations, langCode, { [field]: value })
}

// 翻訳データ全体を更新するヘルパー関数
export const updateStationTranslations = (
  translations: StationTranslation[] | undefined,
  langCode: SupportedLanguageCode,
  field: 'name' | 'extra' | 'fullAddress',
  value: string
): StationTranslation[] => {
  const currentTranslations = translations || []
  const existingTranslation = currentTranslations.find(t => t.langCode === langCode)
  
  if (existingTranslation) {
    // 既存の翻訳を更新
    return currentTranslations.map(t =>
      t.langCode === langCode
        ? { ...t, [field]: value }
        : t
    )
  } else {
    // 新しい翻訳を追加
    return [...currentTranslations, createStationTranslation(langCode, field, value)]
  }
}